import { HTMLAttributes, ReactNode } from 'react';

import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { sortAlphabeticallyBy } from 'utils/misc';

import { FilterOptionsState, TextField } from '@mui/material';
import { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';

import VirtualizedAutocomplete from '../Autocomplete/VirtualizedAutocomplete';

type Props<T> = {
  control: Control<any>;
  name: string;
  label: string;
  variant?: 'standard' | 'outlined';
  size?: 'small' | 'medium';
  disabled?: boolean;
  disableSorting?: boolean;
  disableClearable?: boolean;
  multiple?: boolean;
  options: T[];
  idProperty?: string;
  optionLabelProp?: string;
  sortByProp?: string;
  fullWidth?: boolean;
  onChange?: (selectedOption: T) => void;
  getOptionLabel?: (option: T) => void;
  renderOption?: (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: T,
    state: AutocompleteRenderOptionState
  ) => ReactNode;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  limitTags?: number;
};

export default function ControlledAutoComplete<T>({
  control,
  name,
  label,
  options,
  idProperty = 'id',
  optionLabelProp = 'name',
  sortByProp,
  disableSorting,
  disableClearable,
  multiple,
  variant = 'standard',
  size = 'small',
  disabled = false,
  fullWidth = false,
  getOptionLabel: getOptionLabelProp,
  renderOption,
  onChange,
  filterOptions,
  limitTags
}: Props<T>) {
  const sortBy = sortByProp || optionLabelProp;

  const getOptionLabel = (option) => {
    if (getOptionLabelProp) {
      return getOptionLabelProp(option);
    }
    return option[optionLabelProp] || '';
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const isMulti = !!multiple;

        return (
          <VirtualizedAutocomplete
            disabled={disabled}
            autoHighlight
            multiple={multiple}
            limitTags={limitTags}
            filterOptions={filterOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                fullWidth={fullWidth}
                error={!!fieldState?.error?.type}
                helperText={fieldState?.error?.message}
                variant={variant}
                size={size}
              />
            )}
            options={
              disableSorting
                ? options
                : sortAlphabeticallyBy(options as any[], sortBy)
            }
            disableClearable={disableClearable}
            getOptionLabel={getOptionLabel}
            optionLabelProp={optionLabelProp}
            isOptionEqualToValue={(a, b) => a[idProperty] === b[idProperty]}
            value={
              isMulti
                ? Array.isArray(field.value)
                  ? field.value
                  : []
                : field.value ?? null
            }
            renderOption={renderOption}
            onChange={(e, v) => {
              field.onChange(v);
              onChange?.(v as T);
            }}
          />
        );
      }}
    />
  );
}
